import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WHATSAPP_URL } from '../../../constants/whatsapp';

const VIDEOS = [
  'https://storage.readdy-site.link/project_files/21c16d12-4c00-4be0-9741-73f44fdf52b5/d154ceaf-4100-4dfc-b2ef-99829341be2f_download-3.mp4?v=c218c7383fc44f13a5500235d8952b3e',
  'https://storage.readdy-site.link/project_files/21c16d12-4c00-4be0-9741-73f44fdf52b5/d154ceaf-4100-4dfc-b2ef-99829341be2f_download-3.mp4?v=aa5fca4ab2126cdb75670e3f5a360067',
  'https://storage.readdy-site.link/project_files/21c16d12-4c00-4be0-9741-73f44fdf52b5/7d018b0b-5a07-4d6b-8704-36e0d09a3b9a_download-2.mp4?v=325435166287fb99296fbb27a6653caa',
  'https://storage.readdy-site.link/project_files/21c16d12-4c00-4be0-9741-73f44fdf52b5/bc72d2d1-79b1-47e2-be5b-1104d8144e78_Untitled-design-2.mp4?v=92d631f86c2325f0de577ebb4cc06eb2',
];

// High-quality static poster images used on mobile / slow connections
const POSTERS = [
  'https://public.readdy.ai/ai/img_res/14d6066e31880847834a1a57bdf97ce4.jpg',
  'https://readdy.ai/api/search-image?query=medical%20professional%20putting%20on%20latex%20examination%20gloves%20in%20a%20hospital%20setting%2C%20clean%20clinical%20environment%2C%20dark%20navy%20background%2C%20dramatic%20studio%20lighting%2C%20cinematic%20wide%20shot%2C%20ultra%20sharp%2C%20photorealistic%2C%20high%20resolution%20commercial%20healthcare%20photography%2C%20GCC%20region&width=1920&height=1080&seq=hero-poster-2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=food%20industry%20worker%20wearing%20vinyl%20gloves%20preparing%20food%20in%20a%20commercial%20kitchen%2C%20dark%20teal%20navy%20background%2C%20dramatic%20professional%20lighting%2C%20cinematic%20wide%20shot%2C%20ultra%20sharp%2C%20photorealistic%2C%20high%20resolution%20commercial%20food%20industry%20photography%2C%20Oman%20GCC&width=1920&height=1080&seq=hero-poster-3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=close%20up%20of%20bulk%20disposable%20nitrile%20gloves%20wholesale%20packaging%20boxes%20stacked%20in%20a%20clean%20warehouse%2C%20dark%20charcoal%20background%2C%20dramatic%20studio%20lighting%2C%20cinematic%20wide%20shot%2C%20ultra%20sharp%2C%20photorealistic%2C%20high%20resolution%20commercial%20product%20photography%2C%20Oman%20supply%20distributor&width=1920&height=1080&seq=hero-poster-4&orientation=landscape',
];

/** Returns true when we should skip video (mobile screen or slow network) */
function shouldSkipVideo(): boolean {
  // Skip on small screens
  if (typeof window !== 'undefined' && window.innerWidth < 768) return true;
  // Skip on slow connections (2G / Save-Data)
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string; saveData?: boolean };
  };
  const conn = nav.connection;
  if (conn) {
    if (conn.saveData) return true;
    if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') return true;
  }
  return false;
}

export default function HeroSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [useStaticImage, setUseStaticImage] = useState(false);

  const currentVideoRef = useRef<HTMLVideoElement>(null);
  const nextVideoRef = useRef<HTMLVideoElement>(null);

  // Decide video vs image once on mount
  useEffect(() => {
    setUseStaticImage(shouldSkipVideo());
  }, []);

  // Static image carousel (mobile / slow connection)
  useEffect(() => {
    if (!useStaticImage) return;
    const interval = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % POSTERS.length);
        setTransitioning(false);
      }, 600);
    }, 5000);
    return () => clearInterval(interval);
  }, [useStaticImage]);

  // Video logic (desktop / fast connection)
  const handleVideoEnded = () => {
    const next = (currentIndex + 1) % VIDEOS.length;
    const afterNext = (next + 1) % VIDEOS.length;
    setTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(next);
      setNextIndex(afterNext);
      setTransitioning(false);
    }, 800);
  };

  useEffect(() => {
    if (useStaticImage) return;
    const vid = currentVideoRef.current;
    if (vid) {
      vid.src = VIDEOS[currentIndex];
      vid.load();
      vid.play().catch(() => {});
    }
    const nextVid = nextVideoRef.current;
    if (nextVid) {
      const timer = setTimeout(() => {
        if (nextVid.getAttribute('data-preloaded') !== 'true') {
          nextVid.preload = 'metadata';
          nextVid.src = VIDEOS[nextIndex];
          nextVid.load();
          nextVid.setAttribute('data-preloaded', 'true');
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, useStaticImage]);

  useEffect(() => {
    if (useStaticImage) return;
    const vid = nextVideoRef.current;
    if (vid) {
      vid.preload = 'none';
      vid.src = '';
      vid.removeAttribute('data-preloaded');
    }
  }, [nextIndex, useStaticImage]);

  const goToSlide = (idx: number) => {
    if (idx === currentIndex) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(idx);
      if (!useStaticImage) setNextIndex((idx + 1) % VIDEOS.length);
      setTransitioning(false);
    }, 500);
  };

  const slideCount = useStaticImage ? POSTERS.length : VIDEOS.length;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background layer */}
      <div className="absolute inset-0 z-0">
        {useStaticImage ? (
          /* ── MOBILE / SLOW CONNECTION: static image ── */
          <img
            src={POSTERS[currentIndex]}
            alt="Wholesale glove supplier in Oman"
            loading="eager"
            fetchPriority="high"
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
              transitioning ? 'opacity-0' : 'opacity-100'
            }`}
          />
        ) : (
          /* ── DESKTOP: video carousel ── */
          <>
            <video
              ref={currentVideoRef}
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={handleVideoEnded}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
                transitioning ? 'opacity-0' : 'opacity-100'
              }`}
            >
              <source src={VIDEOS[currentIndex]} type="video/mp4" />
            </video>

            {/* Hidden preload buffer for next video */}
            <video
              ref={nextVideoRef}
              muted
              playsInline
              preload="none"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-0 pointer-events-none"
            />
          </>
        )}

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/20 via-navy/10 to-navy/25" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full flex flex-col justify-center px-6 md:px-16 lg:px-24 pt-28 pb-20">
        <div className="w-full max-w-3xl fade-in-up">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-brand-green/30 border border-brand-green/60 text-white text-sm font-semibold px-5 py-2.5 rounded-full mb-8 tracking-wide">
            <i className="ri-shield-check-fill text-brand-green text-base" />
            Established in Oman — Your Trusted Wholesale Supplier
          </div>

          {/* H1 */}
          <h1 className="font-heading font-black text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-6">
            Oman&apos;s Trusted<br />
            Wholesale<br />
            <span className="text-brand-green">Glove Supplier</span>
          </h1>

          {/* Sub */}
          <p className="text-white/90 text-lg md:text-xl max-w-xl mb-3 leading-relaxed">
            Supplying vinyl, latex &amp; nitrile gloves to hospitals, factories,
            food businesses &amp; labs across Oman and the GCC — bulk orders, fast delivery.
          </p>
          <p className="font-arabic text-white/85 text-base md:text-lg mb-10" dir="rtl">
            نورد القفازات الطبية والصناعية والغذائية بالجملة في عُمان والخليج
          </p>

          {/* Stats row */}
          <div className="flex flex-wrap gap-6 mb-10">
            {[
              { icon: 'ri-group-line', val: '500+', label: 'Wholesale Clients' },
              { icon: 'ri-global-line', val: 'GCC Wide', label: 'Delivery Coverage' },
            ].map(({ icon, val, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 border border-white/20">
                  <i className={`${icon} text-brand-green text-lg`} />
                </div>
                <div>
                  <div className="text-white font-heading font-black text-xl leading-none">{val}</div>
                  <div className="text-white/80 text-xs mt-0.5 whitespace-nowrap">{label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform w-full sm:w-auto justify-center"
            >
              <i className="ri-whatsapp-line text-xl" />
              WhatsApp for Wholesale Price
            </a>
            <Link
              to="/request-quote"
              className="bg-white text-navy font-heading font-bold px-8 py-4 rounded-full shadow-xl hover:bg-gray-100 hover:scale-105 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto flex items-center justify-center gap-2 text-base"
            >
              <i className="ri-file-list-3-line text-xl" />
              Request a Quote
            </Link>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="mt-14 flex flex-col items-start gap-2 text-white/75">
          <p className="text-xs font-medium tracking-widest uppercase">Scroll to explore</p>
          <i className="ri-arrow-down-line text-xl animate-bounce" />
        </div>
      </div>

      {/* Slide indicator dots */}
      <div
        role="group"
        aria-label="Slide navigation"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2"
      >
        {Array.from({ length: slideCount }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`cursor-pointer transition-all duration-300 rounded-full ${
              idx === currentIndex
                ? 'w-8 h-2 bg-brand-green'
                : 'w-2 h-2 bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${idx + 1} of ${slideCount}`}
            aria-current={idx === currentIndex ? true : undefined}
          />
        ))}
      </div>
    </section>
  );
}
